import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  CircularProgress, 
  Alert, 
  Chip,
  Paper,
  Button,
  Divider,
  Stack
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  PlayArrow as ActiveIcon,
  Error as ErrorIcon,
  Replay as RetryIcon,
  CheckCircle as CompletedIcon,
  Pause as PausedIcon,
  Storage as RedisIcon,
  Queue as QueueIcon,
  ChildCare as ChildIcon
} from '@mui/icons-material';
import axiosInstance from "../utils/axiosInstance";
import apiConfig from "../config/appConfig";

const QueueStatus = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(apiConfig.endpoints.queue.status);
      setStatus(response.data);
      setLastUpdated(new Date());
    } catch (err) {
      setError('Failed to fetch queue status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, []);

  if (loading && !status) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading queue status...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={fetchStatus}>
            <RefreshIcon sx={{ mr: 1 }} />
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  const { stats, redisStatus } = status || {};

  const MetricCard = ({ title, value, icon: Icon, color = 'primary', subtitle }) => (
    <Card sx={{ 
      height: '100%', 
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: 3
      }
    }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
          <Icon color={color} sx={{ fontSize: 28 }} />
          <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
            {title}
          </Typography>
        </Stack>
        <Typography variant="h4" fontWeight="bold" color={color === 'error' && value > 0 ? 'error.main' : 'text.primary'}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 4 }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Viber Message Queue Status
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
              Real-time monitoring of message processing queue
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={fetchStatus}
            disabled={loading}
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Stack>
        {lastUpdated && (
          <Typography variant="caption" sx={{ opacity: 0.8, mt: 1, display: 'block' }}>
            Last updated: {lastUpdated.toLocaleTimeString()}
          </Typography>
        )}
      </Paper>

      {/* Main Queue Metrics */}
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Queue Metrics</Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Waiting"
            value={stats?.waiting || 0}
            icon={ScheduleIcon}
            color="info"
            subtitle="Jobs in queue"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Active"
            value={stats?.active || 0}
            icon={ActiveIcon}
            color="success"
            subtitle="Currently processing"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Failed"
            value={stats?.failed || 0}
            icon={ErrorIcon}
            color="error"
            subtitle="Jobs that failed"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Completed"
            value={stats?.completed || 0}
            icon={CompletedIcon}
            color="success"
            subtitle="Successfully processed"
          />
        </Grid>
      </Grid>

      {/* Additional Metrics */}
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Additional Information</Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Retried"
            value={stats?.failedWithRetry || 0}
            icon={RetryIcon}
            color="warning"
            subtitle="Jobs with retry attempts"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Delayed"
            value={stats?.delayed || 0}
            icon={ScheduleIcon}
            color="info"
            subtitle="Scheduled for later"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Paused"
            value={stats?.paused ? 'Yes' : 'No'}
            icon={PausedIcon}
            color={stats?.paused ? 'warning' : 'success'}
            subtitle="Queue status"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            border: redisStatus === 'connected' ? '2px solid #4caf50' : '2px solid #f44336'
          }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                <RedisIcon color={redisStatus === 'connected' ? 'success' : 'error'} sx={{ fontSize: 28 }} />
                <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                  Redis Status
                </Typography>
              </Stack>
              <Chip 
                label={redisStatus || 'unknown'} 
                color={redisStatus === 'connected' ? 'success' : 'error'}
                sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* System Information */}
      <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>System Information</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <QueueIcon color="action" />
              <Typography variant="body1">
                <strong>Repeatable Jobs:</strong> {stats?.repeatable || 0}
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <ChildIcon color="action" />
              <Typography variant="body1">
                <strong>Waiting Children:</strong> {stats?.waitingChildren || 0}
              </Typography>
            </Stack>
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 2 }} />
        
        <Typography variant="body2" color="text.secondary">
          Queue automatically refreshes every 10 seconds. Click refresh button for immediate update.
        </Typography>
      </Paper>
    </Box>
  );
};

export default QueueStatus;
